import { Reporter } from '../Reporter.js';
import LOG from '../../logger.js';
import { filterNullOrUndefined } from '../../transformations/filterNullOrUndefined.js';
import { getNonFunctionProperties } from '../../transformations/getNonFunctionProperties.js';
export { validConfig } from './types.js';
export var SupportedIntegration;
(function (SupportedIntegration) {
    SupportedIntegration["All"] = "All";
    SupportedIntegration["AdobeAnalytics"] = "Adobe Analytics";
    SupportedIntegration["AmazonKinesis"] = "Amazon Kinesis";
    SupportedIntegration["AWSEventBridge"] = "AWS EventBridge";
    SupportedIntegration["AWSLambda"] = "AWS Lambda";
    SupportedIntegration["GoogleAnalytics"] = "Google Analytics";
    SupportedIntegration["Intercom"] = "Intercom";
    SupportedIntegration["Marketo"] = "Marketo";
    SupportedIntegration["Mixpanel"] = "Mixpanel";
    SupportedIntegration["Salesforce"] = "Salesforce";
    SupportedIntegration["Snowflake"] = "Snowflake";
    SupportedIntegration["Pendo"] = "Pendo";
})(SupportedIntegration || (SupportedIntegration = {}));
function isSupportedIntegration(destination) {
    return Object.values(SupportedIntegration).includes(destination);
}
/**
 * Reporter for sending to Cvent's Customer Data Platform (RudderStack).
 */
export default class CustomerDataPlatformReporter extends Reporter {
    /**
     * Initiate class and start asynchronous loading of @rudderstack/analytics-js
     */
    constructor(config) {
        super(config);
        this.name = 'CustomerDataPlatform';
        this.preInitQueue = [];
        this.importRudderAnalyticsClient = () => import('./client/RudderAnalyticsBrowserClient.js').then(module => module.RudderAnalyticsBrowserClient);
        /**
         * Default destinations are passed to rudderAnalytics.load.
         * Action level destination customization is pass to rudderAnalytics.track
         */
        this.defaultDestinations = {
            [SupportedIntegration.All]: true,
            [SupportedIntegration.Snowflake]: true
        };
        this.writeKey = config.writeKey;
        this.dataPlaneUrl = config.dataPlaneUrl;
        this.loadOptions = config.loadOptions;
        if (config.importRudderAnalyticsClient) {
            this.importRudderAnalyticsClient = config.importRudderAnalyticsClient;
        }
        if (config.defaultDestinations != null && config.defaultDestinations.length > 0) {
            // Disable 'All' destination when destinations are customized
            this.defaultDestinations[SupportedIntegration.All] = false;
            config.defaultDestinations.forEach(destination => {
                if (isSupportedIntegration(destination)) {
                    this.defaultDestinations[destination] = true;
                }
            });
        }
    }
    onReady() {
        LOG.debug(`rudderClient is ready`);
        // Process any queued actions sent during initialization
        if (this.preInitQueue.length > 0) {
            LOG.debug(`running ${this.preInitQueue.length} queued operations`);
            this.preInitQueue.forEach(([action, data, callback]) => this.send(action, data, callback));
            this.preInitQueue = [];
        }
    }
    async loadSDKs() {
        if (this.disabled || this.rudderClient) {
            return;
        }
        const loadOptions = {
            ...this.loadOptions,
            integrations: this.defaultDestinations
        };
        try {
            const Client = await this.importRudderAnalyticsClient();
            this.rudderClient = new Client(this.writeKey, this.dataPlaneUrl, loadOptions);
            const ready = new Promise(resolve => {
                this.rudderClient?.ready(() => {
                    this.onReady();
                    resolve();
                });
            });
            await this.rudderClient.load();
            await ready;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error('There was a problem loading Rudderstack SDK', { cause: err });
            if (this.onError) {
                this.onError(error);
            }
        }
    }
    send(action, data, providedCallback) {
        const integrations = this.actionIntegrations(action);
        const options = integrations
            ? {
                integrations: this.actionIntegrations(action)
            }
            : {};
        const actionData = getNonFunctionProperties(action, ['data', 'id', 'destinations']);
        const payload = filterNullOrUndefined({
            ...actionData,
            ...data.globalActionProperties,
            ...data.userTraits,
            ...action.data
        });
        // We convert the id to a name for to conform to spec
        const name = action.id.replaceAll('.', ' ');
        const callback = providedCallback ||
            (() => {
                LOG.debug(`${name} tracked`, payload);
            });
        if (this.disabled) {
            LOG.info(this.name, `${name} tracked`, payload);
        }
        else if (this.rudderClient) {
            const rudder = this.rudderClient;
            // We use the action name if it exists, otherwise we use the id
            rudder.track(name, payload, options, callback);
        }
        else {
            LOG.debug(`rudderAnalytics not initialized yet, pushing action ${name} to preInitQueue`);
            // Prevent memory leak if rudderAnalytics fails to load
            if (this.preInitQueue.length < 100) {
                this.preInitQueue.push([action, data, callback]);
            }
            else {
                LOG.debug('preInitQueue is full, action dropped', action);
            }
        }
    }
    identify(user) {
        const payload = {
            ...user,
            company: {
                name: user.company
            },
            customTraits: null,
            constructor: undefined
        };
        if (this.disabled) {
            LOG.info(this.name, 'identify', payload);
        }
        else if (this.rudderClient) {
            this.rudderClient.reset();
            this.rudderClient.identify(user.platformUserId, payload);
        }
    }
    alias(id) {
        if (this.disabled) {
            LOG.info(this.name, 'alias', { id });
        }
        else if (this.rudderClient) {
            this.rudderClient.alias(id);
        }
    }
    actionIntegrations(action) {
        // When no action destinations are configured rudderAnalytics will use defaultDestinations passed to the load function
        if (!action.destinations) {
            return undefined;
        }
        const destinations = action.destinations.map(destination => destination.name);
        return [...destinations].reduce((acc, destination) => {
            if (isSupportedIntegration(destination)) {
                return {
                    ...acc,
                    [destination]: true
                };
            }
            return acc;
        }, {
            // If destinations are customized default All to false (can be turned by by including in the destinations array)
            [SupportedIntegration.All]: false,
            // Snowflake is always included.
            [SupportedIntegration.Snowflake]: true
        });
    }
}
//# sourceMappingURL=CustomerDataPlatformReporter.js.map